<template>
  <DialogHeader>
    <template #left>
      <div
        class="font-medium text-green-900 cursor-pointer"
        @click="handleCreateFolderClick"
      >
        New
      </div>
    </template>
    <template #right>
      <div
        class="font-medium text-green-900 cursor-pointer"
        @click="handleClick"
      >
        Select
      </div>
    </template>
    {{ currentFolder?.name }}
  </DialogHeader>
  <DialogContent>
    <div v-if="loading" class="w-full h-full flex items-center justify-center">
      <Spinner :size="16" />
    </div>
    <Table.Base v-else-if="result?.folders.items.length">
      <Table.Row
        v-for="folder in result?.folders.items"
        :key="folder.id"
        class="border-gray-800/8"
        @click="handleFolderClick(folder as Item)"
      >
        <Table.Cell class="px-[22px]">
          <ThumbnailIcon
            class="w-9 h-9 mr-2.5"
            :type="ItemType.Folder"
            :icon-size="36"
          />

          <div v-truncate class="w-full">
            <div data-truncate-target>
              {{ folder.name }}
            </div>
          </div>
        </Table.Cell>
      </Table.Row>
    </Table.Base>
    <div
      v-else
      class="font-secondary w-full h-full flex items-center justify-center text-[14px] opacity-50"
    >
      Select {{ currentFolder?.name }}
    </div>
  </DialogContent>
  <DialogFooter>
    <div class="w-full flex items-center justify-between">
      <div
        class="font-secondary text-[14px] font-medium text-green-700 cursor-pointer"
        @click="handleCreateFolderClick"
      >
        Create folder
      </div>
      <div class="flex items-center">
        <UiButton
          class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
          @click="close()"
        >
          Cancel
        </UiButton>
        <UiButton class="w-1/2 tablet-h:w-auto" primary @click="handleClick">
          Select
        </UiButton>
      </div>
    </div>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, ref } from 'vue'
import { ThumbnailIcon, Table, UiButton } from '@dacr/ui'
import {
  type Item,
  ItemType,
  useFoldersQuery,
  type Folder,
} from '@/shared/graphql/legacy/generated'
import Spinner from '@/shared/components/Spinner.vue'

const { close, open } = useDialog()

const path = ref<Item[]>([
  {
    name: 'Dacr',
    id: 'root',
  } as Item,
])

const currentFolder = computed(() => path.value.at(-1))

const variables = computed(() => ({
  id: currentFolder.value?.id || 'root',
}))

const { result, loading } = useFoldersQuery(variables, {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-only',
})

const handleFolderClick = (item: Item) => {
  path.value.push(item)
}

const handleCreateFolderClick = () => {
  open(Dialogs.AddFolder, {
    props: {
      folderId: currentFolder.value?.id,
      handler(folder: Folder) {
        handleFolderClick({
          id: folder.id,
          name: folder.name,
        } as Item)
      },
    },
    stack: true,
  })
}

const handleClick = async () => {
  if (!currentFolder.value) return

  close(currentFolder.value)
}
</script>
